import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as Yup from 'yup';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import { parsePhoneNumber } from 'libphonenumber-js';
import 'react-phone-number-input/style.css';
import { apiUrls, API_CONFIG } from '../config/api';
import { getFinalCanSellState, getDisabledReason } from '../config/features';
import DeleteIcon from '@mui/icons-material/Delete';
import { useIframeResize } from '../hooks/useIframeResize';
import PaymentResult from './PaymentResult';


const validationSchema = Yup.object({
  firstName: Yup.string()
    .min(2, 'Ім\'я повинно містити принаймні 2 символи')
    .max(50, 'Ім\'я не може містити більше 50 символів')
    .required('Ім\'я обов\'язкове'),
  lastName: Yup.string()
    .min(2, 'Прізвище повинно містити принаймні 2 символи')
    .max(50, 'Прізвище не може містити більше 50 символів')
    .required('Прізвище обов\'язкове'),
  email: Yup.string()
    .email('Невірний формат електронної пошти')
    .required('Електронна пошта обов\'язкова'),
  phone: Yup.string()
    .matches(/^[\+]?[0-9\s\-\(\)]{10,15}$/, 'Невірний формат номера телефону')
    .required('Номер телефону обов\'язковий')
});

const PARTNER_EMAIL_SUFFIXES = ['@softserveinc.com'];

// Product table row component
const ProductTableRow = ({ product, quantity, cardId, onDeleteItem, seasonData }) => {
  if (!product) return null;

  // Ensure price is a valid number, fallback to 0 if undefined/null/NaN
  const productPrice = product.price && !isNaN(product.price) ? Number(product.price) : 0;
  const itemTotal = productPrice * quantity;
  
  // Helper function for correct Ukrainian declension
  const getDaysText = (days) => {
    if (days === 1) return 'день повний';
    if (days >= 2 && days <= 4) return 'дні';
    return 'днів';
  };
  
  // Find season for this product
  // Extract skiDataId from cardId (format: "skiDataId_price")
  const parts = cardId.split('_');
  const priceStr = parts.pop();
  const skiDataId = parts.join('_');
  
  let seasonTitle = '';
  if (seasonData) {
    for (const seasonKey of Object.keys(seasonData)) {
      const season = seasonData[seasonKey];
      const productPrice = product.price && !isNaN(product.price) ? Number(product.price) : 0;
      if (season.dailyPasses?.some(p => {
          const pPrice = p.price && !isNaN(p.price) ? Number(p.price) : 0;
          return p.skiDataId === skiDataId && pPrice === productPrice;
        }) || 
          season.hourlyPasses?.some(p => {
          const pPrice = p.price && !isNaN(p.price) ? Number(p.price) : 0;
          return p.skiDataId === skiDataId && pPrice === productPrice;
        })) {
        seasonTitle = season.title || 
          (seasonKey === 'holiday' ? 'Святковий сезон' : 
           seasonKey === 'winter' ? 'Зимовий сезон' : 'Низький сезон');
        break;
      }
    }
  }
  
  const productDisplayName = product.days 
    ? `${product.days} ${getDaysText(product.days)}` 
    : product.type;
  const productDetails = [
    product.time,
    product.subtype
  ].filter(Boolean).join(' ');

  return (
    <div className="skipass-table-row">
      <div className="skipass-product-info">
        {productDisplayName}
        {productDetails && ` ${productDetails}`}
        {seasonTitle && ` - ${seasonTitle}`}
        <span className="skipass-quantity-multiplier"> * {quantity}</span>
      </div>
      <div className="skipass-product-actions">
        <span className="skipass-product-price">{itemTotal.toFixed(0)} ₴</span>
        <button 
          className="skipass-delete-item-btn"
          onClick={() => onDeleteItem(cardId)}
        >
          <DeleteIcon fontSize="small" />
        </button>
      </div>
    </div>
  );
};

// Product table component
const ProductTable = ({ 
  selectedCards, 
  cardQuantities,
  findProductById, 
  seasonData, 
  onDeleteItem, 
  showHeader = true,
  className = "product-table"
}) => {
  return (
    <div className={className}>
      {showHeader && (
        <div className="skipass-table-header">
          <span className="skipass-header-product">Товар</span>
          <span className="skipass-header-subtotal">Проміжний підсумок</span>
        </div>
      )}
      
      <div className="skipass-table-body">
        {Array.from(selectedCards).map(cardId => {
          const quantity = cardQuantities[cardId] || 1;
          const product = findProductById(cardId);
          if (!product) {
            // Skip if product not found (might be due to format change)
            return null;
          }
          return (
            <ProductTableRow
              key={cardId}
              product={product}
              quantity={quantity}
              cardId={cardId}
              onDeleteItem={onDeleteItem}
              seasonData={seasonData}
            />
          );
        })}
      </div>
    </div>
  );
};

const PricingPageSimple = () => {
  const hasFetched = React.useRef(false);
  const containerRef = useRef(null);
  
  const { sendHeight } = useIframeResize(containerRef);
  const cartActionsRef = useRef(null);
  
  const urlParams = new URLSearchParams(window.location.search);
  const canSellParam = urlParams.get('can_sell');
  
  const [seasonData, setSeasonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [backendCanSell, setBackendCanSell] = useState(true);
  const [backendSettings, setBackendSettings] = useState({
    can_sell: true,
    can_sell_holiday_season: true,
    can_sell_winter_season: true,
    can_sell_low_season: true
  });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [messengerConfig, setMessengerConfig] = useState(null);
  const [paymentResultOrderId, setPaymentResultOrderId] = useState(null);
  const [discountCheck, setDiscountCheck] = useState({ enabled: false, eligible: false, percent: 10 });
  const [discountCode, setDiscountCode] = useState('');
  const [discountValidated, setDiscountValidated] = useState(false);
  const [discountPercentApplied, setDiscountPercentApplied] = useState(0);
  const [discountError, setDiscountError] = useState('');
  const [discountAlreadyHasCode, setDiscountAlreadyHasCode] = useState(false);
  const [discountCodeSent, setDiscountCodeSent] = useState(false);
  const [sendingDiscountCode, setSendingDiscountCode] = useState(false);
  const [validatingDiscountCode, setValidatingDiscountCode] = useState(false);
  const discountSummaryRef = useRef(null);

  useEffect(() => {
    const fetchMessengerConfig = async () => {
      try {
        const response = await fetch(`${API_CONFIG.baseURL}/api/messengers/config`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.hasAnyMessenger) {
            setMessengerConfig(data.config);
            setTimeout(() => {
              if (sendHeight) {
                sendHeight();
              }
            }, 100);
          }
        }
      } catch (error) {
      }
    };

    fetchMessengerConfig();
  }, []);

  useEffect(() => {
    setDiscountCodeSent(false);
    discountSummaryRef.current = null;
  }, [formData.email]);

  useEffect(() => {
    const email = (formData.email || '').trim().toLowerCase();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setDiscountCheck(prev => ({ ...prev, eligible: false }));
      return;
    }
    if (!PARTNER_EMAIL_SUFFIXES.some(suffix => email.endsWith(suffix))) {
      setDiscountCheck(prev => ({ ...prev, eligible: false }));
      return;
    }
    const check = async () => {
      try {
        const res = await fetch(`${API_CONFIG.baseURL}/api/discount/check?email=${encodeURIComponent(email)}`);
        const data = await res.json();
        if (data.success && data.data) {
          setDiscountCheck({
            enabled: !!data.data.enabled,
            eligible: !!data.data.eligible,
            percent: data.data.percent ?? 10,
            alreadyValidated: !!data.data.alreadyValidated,
            maxAttemptsReached: !!data.data.maxAttemptsReached
          });
          if (data.data.maxAttemptsReached) {
            discountSummaryRef.current = null;
            setDiscountValidated(false);
            setDiscountPercentApplied(0);
          } else if (data.data.alreadyValidated) {
            const pct = data.data.percent ?? 10;
            discountSummaryRef.current = { hasDiscount: true, percent: pct };
            setDiscountValidated(true);
            setDiscountPercentApplied(pct);
          } else {
            discountSummaryRef.current = null;
            setDiscountValidated(false);
            setDiscountPercentApplied(0);
          }
        }
      } catch (e) {
        setDiscountCheck(prev => ({ ...prev, eligible: false }));
      }
    };
    check();
  }, [formData.email]);

  useEffect(() => {
    if (selectedProduct) {
      setTimeout(() => {
        const formPage = document.querySelector('.skipass-form-page');
        if (formPage) {
          formPage.scrollIntoView({ behavior: 'smooth', block: 'start' });
          if (sendHeight) {
            setTimeout(() => {
              sendHeight();
            }, 300);
          }
        }
      }, 100);
    }
  }, [selectedProduct, sendHeight]);

  // Form validation function
  const validateForm = () => {
    const errors = {};
    
    // Email validation
    if (!formData.email) {
      errors.email = "Email є обов'язковим";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Введіть коректний email";
    }
    
    
    if (!formData.phone) {
      errors.phone = "Номер телефону є обов'язковим";
    } else if (!isValidPhoneNumber(formData.phone)) {
      errors.phone = "Введіть коректний номер телефону";
    } else {
      try {
        
        const phoneNumber = parsePhoneNumber(formData.phone);
        const nationalLength = phoneNumber.nationalNumber.length;
        const country = phoneNumber.country;
        
        
        if (country === 'UA') {
          
          if (nationalLength !== 9) {
            errors.phone = "Український номер повинен містити 9 цифр після коду країни";
          }
        } else {
          
          if (nationalLength > 10) {
            errors.phone = "Номер телефону занадто довгий для цієї країни";
          } else if (nationalLength < 6) {
            errors.phone = "Номер телефону занадто короткий для цієї країни";
          }
        }
      } catch (parseError) {
        
        const digitsOnly = formData.phone.replace(/\D/g, '');
        if (digitsOnly.length > 13) {
          errors.phone = "Номер телефону занадто довгий";
        } else if (digitsOnly.length < 7) {
          errors.phone = "Номер телефону занадто короткий";
        }
      }
    }
    
    // Terms validation
    if (!isTermsAccepted) {
      errors.terms = "Необхідно прийняти умови";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Check if form is valid
  const isFormValid = () => {
    if (!formData.email || !formData.phone || !isTermsAccepted) {
      return false;
    }
    
    // Email validation
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      return false;
    }
    
    // Phone validation using built-in validation + additional checks
    if (!isValidPhoneNumber(formData.phone)) {
      return false;
    }
    
    try {
      // Additional validation using libphonenumber-js
      const phoneNumber = parsePhoneNumber(formData.phone);
      const nationalLength = phoneNumber.nationalNumber.length;
      const country = phoneNumber.country;
      
      // Country-specific validation
      if (country === 'UA') {
        
        if (nationalLength !== 9) {
          return false;
        }
      } else {
        
        if (nationalLength > 10 || nationalLength < 6) {
          return false;
        }
      }
    } catch (parseError) {
      
      const digitsOnly = formData.phone.replace(/\D/g, '');
      if (digitsOnly.length > 13 || digitsOnly.length < 7) {
        return false;
      }
    }
    
    return true;
  };
  const [currentOrderId, setCurrentOrderId] = useState(null);

  // Quantity control state
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [cardQuantities, setCardQuantities] = useState({});
  
  // Configuration - Maximum items that can be selected
  const MAX_ITEMS_LIMIT = 50; // Total maximum items
  const MAX_PER_CARD_TYPE = 10; // Maximum per individual card type

  // Helper function to check if product can be sold
  // Priority: 1. Global can_sell > 2. Season flag > 3. Date check (only if season flag is true)
  const canSellProduct = (product, seasonKey = null) => {
    // 1. Check global can_sell flag first
    if (!backendSettings.can_sell) {
      return false;
    }
    
    // 2. Check season-specific flag (has priority over date check)
    if (seasonKey) {
      const seasonFlagMap = {
        'holiday': 'can_sell_holiday_season',
        'winter': 'can_sell_winter_season',
        'low': 'can_sell_low_season'
      };
      
      const flagName = seasonFlagMap[seasonKey];
      if (flagName) {
        const seasonFlagValue = backendSettings[flagName];
        if (seasonFlagValue === false) {
          return false;
        }
      }
      
      // 3. Check date restriction (5 days before season end) - only if season flag is true
      if (product && product.validityRange && product.validityRange.end) {
        const seasonEndDate = new Date(product.validityRange.end);
        const today = new Date();

        if (product.time) {
          const timeMapping = {
            "09:00 - 13:00": 13,
            "17:00 - 21:00": 21
          };
          const newHour = timeMapping[product.time];
          if (newHour !== undefined) {
            seasonEndDate.setHours(newHour);
          } else {
            seasonEndDate.setHours(17);
          }
        }

        var days = 1;
        if (product.days)
          days = product.days;

        const daysUntilEnd = Math.ceil((seasonEndDate - today) / (1000 * 60 * 60 * 24));
        
        // If user cannot fully use the ticket block him from buying
        if (daysUntilEnd < days) {
          return false;
        }
      }
    }
    
    return true;
  };

  // Compute final CAN_SELL state (frontend + backend)
  const canSell = getFinalCanSellState(backendCanSell);
  const disabledReason = getDisabledReason(backendCanSell);

  // Filter states
  const [expandedSeasons, setExpandedSeasons] = useState({
    holiday: false,
    winter: false,
    low: false
  });

  const [selectedFilters, setSelectedFilters] = useState({
    holiday: false,
    winter: false,
    low: false
  });

  const [durationFilters, setDurationFilters] = useState({});
  const [availableDurations, setAvailableDurations] = useState([]);

  const [showSeasonFilters, setShowSeasonFilters] = useState(false);
  const [showDurationFilters, setShowDurationFilters] = useState(false);

  

  
  const mockSeasonData = {
    holiday: {
      title: 'Святковий сезон',
      dates: '23 грудня 2024 - 8 січня 2025',
      priceFrom: 1450,
      dailyPasses: [
        { days: 2, price: 3900, oldPrice: 2900, savings: 100 },
        { days: 3, price: 5700, oldPrice: 4350, savings: 150 },
        { days: 4, price: 7200, oldPrice: 5800, savings: 400 },
        { days: 5, price: 8500, oldPrice: 7250, savings: 750 },
        { days: 6, price: 9600, oldPrice: 8700, savings: 900 },
        { days: 7, price: 10500, oldPrice: 10150, savings: 1150 }
      ],
      hourlyPasses: [
        { type: '1 день повний', time: '09:00-17:00', price: 1450 },
        { type: '1 день вечірній', time: '13:00-17:00', price: 1100 },
        { type: '1 день ранковий', time: '09:00-13:00', price: 1100 },
        { type: '4 години', time: '09:00-13:00', price: 900 },
        { type: '4 години', time: '13:00-17:00', price: 900 }
      ]
    },
    winter: {
      title: 'Зимовий сезон',
      dates: '9 січня - 16 березня 2025',
      priceFrom: 1200,
      dailyPasses: [
        { days: 2, price: 3200, oldPrice: 2400, savings: 400 },
        { days: 3, price: 4500, oldPrice: 3600, savings: 300 },
        { days: 4, price: 5600, oldPrice: 4800, savings: 400 },
        { days: 5, price: 6500, oldPrice: 6000, savings: 500 },
        { days: 6, price: 7200, oldPrice: 7200, savings: 600 },
        { days: 7, price: 7700, oldPrice: 8400, savings: 700 }
      ],
      hourlyPasses: [
        { type: '1 день повний', time: '09:00-17:00', price: 1200 },
        { type: '1 день вечірний', time: '13:00-17:00', price: 900 },
        { type: '1 день ранковий', time: '09:00-13:00', price: 900 },
        { type: '4 години', time: '09:00-13:00', price: 750 },
        { type: '4 години', time: '13:00-17:00', price: 750 }
      ]
    },
    low: {
      title: 'Низький сезон',
      dates: '17 березня - 31 березня 2025',
      priceFrom: 800,
      dailyPasses: [
        { days: 2, price: 2000, oldPrice: 1600, savings: 400 },
        { days: 3, price: 2700, oldPrice: 2400, savings: 300 },
        { days: 4, price: 3200, oldPrice: 3200, savings: 400 },
        { days: 5, price: 3500, oldPrice: 4000, savings: 500 },
        { days: 6, price: 3600, oldPrice: 4800, savings: 600 },
        { days: 7, price: 3700, oldPrice: 5600, savings: 700 }
      ],
      hourlyPasses: [
        { type: '1 день повний', time: '09:00-17:00', price: 800 },
        { type: '1 день вечірний', time: '13:00-17:00', price: 600 },
        { type: '1 день ранковий', time: '09:00-13:00', price: 600 },
        { type: '4 години', time: '09:00-13:00', price: 500 },
        { type: '4 години', time: '13:00-17:00', price: 500 }
      ]
    }
  };

  
  useEffect(() => {
    const fetchPricingData = async () => {
      if (hasFetched.current) return;
      hasFetched.current = true;

      try {
        setLoading(true);
        const baseUrl = API_CONFIG.baseURL;
        const pricingUrl = canSellParam === 'false' 
          ? `${baseUrl}/api/pricing?can_sell=false`
          : `${baseUrl}/api/pricing`;

        const response = await fetch(pricingUrl);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.success && result.data) {
          setSeasonData(result.data);
          
          if (typeof result.canSell === 'boolean') {
            setBackendCanSell(result.canSell);
          }
          
          // Update backendSettings from API response
          if (result.settings) {
            setBackendSettings(prev => ({
              can_sell: result.settings.can_sell ?? prev.can_sell,
              can_sell_holiday_season: result.settings.can_sell_holiday_season ?? prev.can_sell_holiday_season,
              can_sell_winter_season: result.settings.can_sell_winter_season ?? prev.can_sell_winter_season,
              can_sell_low_season: result.settings.can_sell_low_season ?? prev.can_sell_low_season
            }));
          }
          
          if (result.filters && result.filters.durations) {
            // Sort durations by number of days
            const sortedDurations = [...result.filters.durations].sort((a, b) => {
              const daysA = parseInt(a) || 0;
              const daysB = parseInt(b) || 0;
              return daysA - daysB;
            });
            setAvailableDurations(sortedDurations);
            
            const newDurationFilters = {};
            sortedDurations.forEach(duration => {
              newDurationFilters[duration] = false;
            });
            setDurationFilters(newDurationFilters);
          }
        } else {
          console.warn('Backend response indicates failure, using mock data');
          setSeasonData(mockSeasonData);
        }
      } catch (error) {
        console.error('Error fetching pricing data:', error);
        setSeasonData(mockSeasonData);
      } finally {
        setLoading(false);
      }
    };

    fetchPricingData();
  }, []);

  // Socket.IO connection for real-time settings updates
  useEffect(() => {
    if (window.__SKI_PASS_WEB_COMPONENT__) {
      return;
    }
    
    // Import socket.io-client dynamically
    import('socket.io-client').then(({ default: io }) => {
      // Use socket URL (can be different from API URL for CloudFront/Amplify)
      // Socket.IO should connect directly to backend, not through CloudFront
      const socketUrl = API_CONFIG.socketURL || API_CONFIG.baseURL || 'http://localhost:5001';
      
      // Don't connect to localhost in production
      const isProduction = process.env.NODE_ENV === 'production';
      if (isProduction && socketUrl.includes('localhost')) {
        console.warn('⚠️ Socket.IO: Cannot connect to localhost in production. Please set REACT_APP_SOCKET_URL or REACT_APP_API_BASE_URL environment variable.');
        return;
      }
      
      const socket = io(socketUrl, {
        // Use polling ONLY - bypass CloudFront, connect directly to backend
        // Polling is more reliable and doesn't require WebSocket support
        transports: ['polling'],
        // Disable upgrade to WebSocket - use polling only
        upgrade: false,
        // Reconnection settings
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: Infinity, // Allow infinite reconnection attempts
        timeout: 20000, // Connection timeout (20 seconds)
        // Additional options
        forceNew: false,
        // Path for Socket.IO (default is /socket.io/)
        path: '/socket.io/',
        // Query parameters if needed
        query: {},
        // Polling-specific options to prevent long pending requests
        polling: {
          extraHeaders: {}
        },
        // Auto-connect: false to prevent automatic connection attempts
        autoConnect: true,
        // Force new connection on 400 errors (invalid session ID)
        // This ensures client reconnects with a fresh session
        rejectUnauthorized: false
      });

      socket.on('connect', () => {
        // Connected successfully - no log needed
      });
      
      socket.on('connect_error', (error) => {
        // "Session ID unknown" is expected when session expires or server restarts
        // Client will automatically reconnect with a new session
        const isSessionIdUnknown = error.message && (
          error.message.includes('Session ID unknown') ||
          error.message.includes('session ID unknown') ||
          error.message.includes('Unknown session') ||
          error.message.includes('xhr poll error')
        );
        
        // 400 Bad Request usually means invalid session ID
        const isBadRequest = error.type === 'TransportError' || 
                            (error.message && error.message.includes('400')) ||
                            (error.message && error.message.includes('Bad Request'));
        
        if (isSessionIdUnknown || isBadRequest) {
          // Force reconnection with new session ID
          if (socket.connected) {
            socket.disconnect();
          }
          // Client will automatically reconnect
          return;
        }
        
        console.warn('Socket.IO connection error (non-critical):', error.message);
        if (socket.recovered) {
          socket.disconnect();
        }
      });

      socket.on('settings-updated', (updatedSettings) => {
        // Use functional update to avoid stale closure
        setBackendSettings(prev => ({
          can_sell: updatedSettings.can_sell !== undefined ? updatedSettings.can_sell : prev.can_sell,
          can_sell_holiday_season: updatedSettings.can_sell_holiday_season !== undefined ? updatedSettings.can_sell_holiday_season : prev.can_sell_holiday_season,
          can_sell_winter_season: updatedSettings.can_sell_winter_season !== undefined ? updatedSettings.can_sell_winter_season : prev.can_sell_winter_season,
          can_sell_low_season: updatedSettings.can_sell_low_season !== undefined ? updatedSettings.can_sell_low_season : prev.can_sell_low_season
        }));
        // Also update backendCanSell
        if (typeof updatedSettings.can_sell === 'boolean') {
          setBackendCanSell(updatedSettings.can_sell);
        }
      });

      socket.on('disconnect', () => {
        // Disconnected
      });

      return () => {
        socket.disconnect();
      };
    }).catch(err => {
      console.error('Failed to load socket.io-client:', err);
    });
  }, []);

  useEffect(() => {
    if (window.__SKI_PASS_WEB_COMPONENT__) {
      return;
    }
    
    const handleGlobalMessage = (event) => {
      if (event.data && event.data.type === 'show-confirmation' && event.data.orderId) {
        setPaymentResultOrderId(event.data.orderId);
        setSelectedCards(new Set());
        setCardQuantities({});
        setSelectedProduct(null);
      }
    };

    window.addEventListener('message', handleGlobalMessage);

    return () => {
      window.removeEventListener('message', handleGlobalMessage);
    };
  }, []);

  useEffect(() => {
    const updateCartActionsPosition = () => {
      const mainContent = document.querySelector('.skipass-main-content');
      const cartActions = cartActionsRef.current;
      
      if (mainContent && cartActions && selectedCards.size > 0) {
        const mainContentRect = mainContent.getBoundingClientRect();
        
        cartActions.style.left = `${mainContentRect.left}px`;
        cartActions.style.width = `${mainContentRect.width}px`;
        cartActions.style.maxWidth = `${mainContentRect.width}px`;
      }
    };
    
    if (selectedCards.size > 0) {
      updateCartActionsPosition();
      window.addEventListener('resize', updateCartActionsPosition);
      window.addEventListener('scroll', updateCartActionsPosition);
      
      const interval = setInterval(updateCartActionsPosition, 100);
      
      return () => {
        window.removeEventListener('resize', updateCartActionsPosition);
        window.removeEventListener('scroll', updateCartActionsPosition);
        clearInterval(interval);
      };
    }
  }, [selectedCards.size]);

  
  const toggleCardSelection = (cardId) => {
    if (!canSell) {
      return;
    }
    
    // Check if product can still be sold (more than 5 days until season end)
    const product = findProductById(cardId);
    if (product) {
      // Find seasonKey for this product
      let seasonKey = null;
      if (seasonData) {
        for (const key of Object.keys(seasonData)) {
          const season = seasonData[key];
          const found = season.dailyPasses?.some(p => {
            const id = p.skiDataId || `${key}-daily-${season.dailyPasses.indexOf(p)}`;
            return `${id}_${p.price}` === cardId;
          }) || season.hourlyPasses?.some(p => {
            const id = p.skiDataId || `${key}-hourly-${season.hourlyPasses.indexOf(p)}`;
            return `${id}_${p.price}` === cardId;
          });
          if (found) {
            seasonKey = key;
            break;
          }
        }
      }
      if (!canSellProduct(product, seasonKey)) {
        return; // Don't allow selection if cannot sell
      }
    }

    setSelectedCards(prevSelected => {
      const newSet = new Set(prevSelected);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
        
        setCardQuantities(prevQty => {
          const newQty = { ...prevQty };
          delete newQty[cardId];
          return newQty;
        });
      } else {
        newSet.add(cardId);
        
        setCardQuantities(prevQty => ({
          ...prevQty,
          [cardId]: 1
        }));
      }
      return newSet;
    });
  };

  const updateQuantity = (cardId, quantity) => {
    const qty = Math.max(1, parseInt(quantity) || 1);
    
    
    if (qty > MAX_PER_CARD_TYPE) {
      alert(`Максимальна кількість для одного типу товару: ${MAX_PER_CARD_TYPE}`);
      return;
    }
    
    
    const currentTotal = getTotalItems();
    const currentCardQty = cardQuantities[cardId] || 1;
    const difference = qty - currentCardQty;
    
    if (currentTotal + difference > MAX_ITEMS_LIMIT) {
      const maxAllowed = MAX_ITEMS_LIMIT - (currentTotal - currentCardQty);
      alert(`Максимальна загальна кількість товарів: ${MAX_ITEMS_LIMIT}. Для цього товару можна додати ще ${maxAllowed}`);
      return;
    }
    
    setCardQuantities(prev => ({
      ...prev,
      [cardId]: qty
    }));
  };

  const incrementQuantity = (cardId) => {
    const currentQty = cardQuantities[cardId] || 1;
    const currentTotal = getTotalItems();
    
    
    if (currentQty >= MAX_PER_CARD_TYPE) {
      alert(`Максимальна кількість для одного типу товару: ${MAX_PER_CARD_TYPE}`);
      return;
    }
    
    
    if (currentTotal >= MAX_ITEMS_LIMIT) {
      alert(`Досягнуто максимальну кількість товарів: ${MAX_ITEMS_LIMIT}`);
      return;
    }
    
    updateQuantity(cardId, currentQty + 1);
  };

  const decrementQuantity = (cardId) => {
    const currentQty = cardQuantities[cardId] || 1;
    if (currentQty > 1) {
      updateQuantity(cardId, currentQty - 1);
    }
  };

  const findProductById = (cardId) => {
    if (!seasonData || !cardId) return null;
    
    // New format: "skiDataId_price" (e.g., "abc123_20")
    // Try to parse as skiDataId_price format first
    const underscoreParts = cardId.split('_');
    if (underscoreParts.length >= 2) {
      const priceStr = underscoreParts.pop(); // Last part is price
      const skiDataId = underscoreParts.join('_'); // Everything else is skiDataId
      const targetPrice = Number(priceStr);
      
      if (!isNaN(targetPrice)) {
        // Search by skiDataId and price
        for (const seasonKey of Object.keys(seasonData)) {
          const season = seasonData[seasonKey];
          
          // Check daily passes
          if (season.dailyPasses) {
            const dailyProduct = season.dailyPasses.find(p => {
              const pPrice = p.price && !isNaN(p.price) ? Number(p.price) : 0;
              return p.skiDataId === skiDataId && pPrice === targetPrice;
            });
            if (dailyProduct) return dailyProduct;
          }
          
          // Check hourly passes
          if (season.hourlyPasses) {
            const hourlyProduct = season.hourlyPasses.find(p => {
              const pPrice = p.price && !isNaN(p.price) ? Number(p.price) : 0;
              return p.skiDataId === skiDataId && pPrice === targetPrice;
            });
            if (hourlyProduct) return hourlyProduct;
          }
        }
      }
    }
    
    // Fallback: Try to find by skiDataId only (old format or if price parsing failed)
    for (const seasonKey of Object.keys(seasonData)) {
      const season = seasonData[seasonKey];
      
      // Check daily passes
      if (season.dailyPasses) {
        const dailyProduct = season.dailyPasses.find(p => p.skiDataId === cardId);
        if (dailyProduct) return dailyProduct;
      }
      
      // Check hourly passes
      if (season.hourlyPasses) {
        const hourlyProduct = season.hourlyPasses.find(p => p.skiDataId === cardId);
        if (hourlyProduct) return hourlyProduct;
      }
    }
    
    // Fallback to old format (seasonKey-type-index) for backward compatibility
    const parts = cardId.split('-');
    if (parts.length >= 3) {
      const seasonKey = parts[0];
      const type = parts[1];
      const index = parseInt(parts.slice(2).join('-'));
    const season = seasonData[seasonKey];
    if (season) {
        if (type === 'daily' && season.dailyPasses && !isNaN(index)) {
          return season.dailyPasses[index];
        } else if (type === 'hourly' && season.hourlyPasses && !isNaN(index)) {
          return season.hourlyPasses[index];
      }
    }
    }
    
    return null;
  };

  const effectiveDiscountPercent = (discountValidated || discountCheck.alreadyValidated)
    ? (discountPercentApplied || discountCheck.percent || 10)
    : 0;

  const calculateTotalPrice = () => {
    let total = 0;
    selectedCards.forEach(cardId => {
      const product = findProductById(cardId);
        if (product) {
          const quantity = cardQuantities[cardId] || 1;
        const productPrice = product.price && !isNaN(product.price) ? Number(product.price) : 0;
        total += productPrice * quantity;
      }
    });
    if (effectiveDiscountPercent > 0) {
      total = Math.round(total * (1 - effectiveDiscountPercent / 100));
    }
    return total;
  };

  const totalToPay = useMemo(() => calculateTotalPrice(), [
    selectedCards,
    cardQuantities,
    effectiveDiscountPercent,
    discountValidated,
    discountCheck.alreadyValidated,
    discountCheck.percent,
    seasonData
  ]);

  const calculateOriginalTotalPrice = () => {
    let total = 0;
    selectedCards.forEach(cardId => {
      const product = findProductById(cardId);
      if (product) {
        const quantity = cardQuantities[cardId] || 1;
        const productPrice = product.price && !isNaN(product.price) ? Number(product.price) : 0;
        total += productPrice * quantity;
      }
    });
    return total;
  };

  const getTotalItems = () => {
    return Array.from(selectedCards).reduce((total, cardId) => {
      return total + (cardQuantities[cardId] || 1);
    }, 0);
  };

  
  const toggleSeason = (seasonKey) => {
    setExpandedSeasons(prev => ({
      ...prev,
      [seasonKey]: !prev[seasonKey]
    }));
  };

  
  const handleProductSelect = (product, seasonKey, type, index) => {
    if (!canSell || !canSellProduct(product, seasonKey)) {
      
      return;
    }

    // Use combination of skiDataId and price as unique identifier
    // This allows selecting same product with different prices separately
    const price = product.price && !isNaN(product.price) ? Number(product.price) : 0;
    const uniqueId = product.skiDataId || `${seasonKey}-${type}-${index}`;
    const cardId = `${uniqueId}_${price}`;
    toggleCardSelection(cardId);
  };

  const handleSeasonFilter = (season) => {
    setExpandedSeasons(prev => ({
      ...prev,
      [season]: !prev[season]
    }));
    
    setSelectedFilters(prev => ({
      ...prev,
      [season]: !prev[season]
    }));
  };

  const handleDurationFilter = (duration) => {
    setDurationFilters(prev => ({
      ...prev,
      [duration]: !prev[duration]
    }));
  };

  const clearDurationFilters = () => {
    setDurationFilters({
      '2days': false,
      '3days': false,
      '4days': false,
      '5days': false,
      '6days': false,
      '7days': false
    });
  };

  
  // Cart item row component
  const CartItemRow = ({ cardId, product, quantity, seasonData }) => {
          if (!product) return null;
    
    // Extract skiDataId from cardId (format: "skiDataId_price")
    const parts = cardId.split('_');
    const priceStr = parts.pop();
    const skiDataId = parts.join('_');

          const getProductName = () => {
      if (product.days) {
              return `${product.days} днів`;
            } else {
              let name = product.type;
              if (product.subtype) name += ` - ${product.subtype}`;
              if (product.time) name += ` ${product.time}`;
              return name;
            }
          };
    
    // Find season for display
    let season = null;
    for (const seasonKey of Object.keys(seasonData)) {
      const s = seasonData[seasonKey];
      const productPrice = product.price && !isNaN(product.price) ? Number(product.price) : 0;
      if (s.dailyPasses?.some(p => {
          const pPrice = p.price && !isNaN(p.price) ? Number(p.price) : 0;
          return p.skiDataId === skiDataId && pPrice === productPrice;
        }) || 
          s.hourlyPasses?.some(p => {
          const pPrice = p.price && !isNaN(p.price) ? Number(p.price) : 0;
          return p.skiDataId === skiDataId && pPrice === productPrice;
        })) {
        season = s;
        break;
      }
    }

    if (!season) return null;

          return (
      <div className="skipass-cart-summary-row">
              <div className="skipass-cart-summary-left">
                <div className="skipass-cart-summary-icon">🎿</div>
                <span className="skipass-cart-summary-text">{season.title}</span>
              </div>
              <div className="skipass-cart-summary-left">
                <div className="skipass-cart-summary-icon">📅</div>
                <span className="skipass-cart-summary-text">{getProductName()}</span>
              </div>
              <div className="skipass-cart-summary-left">
                <div className="skipass-cart-summary-icon">👤</div>
          <span className="skipass-cart-summary-text">{quantity}x {product.price && !isNaN(product.price) ? Number(product.price).toFixed(0) : '0'} ₴</span>
              </div>
            </div>
    );
  };

  const CartItemsList = () => {
    return (
      <div className="skipass-cart-items-list">
        {Array.from(selectedCards).map(cardId => {
          const product = findProductById(cardId);
          const quantity = cardQuantities[cardId] || 1;
          return (
            <CartItemRow
              key={cardId}
              cardId={cardId}
              product={product}
              quantity={quantity}
              seasonData={seasonData}
            />
          );
        })}
      </div>
    );
  };

  const hasActiveDurationFilters = Object.values(durationFilters).some(filter => filter);

  const getFilteredDailyPasses = (dailyPasses) => {
    
    const hasActiveFilters = Object.values(durationFilters).some(filter => filter);
    if (!hasActiveFilters) {
      return dailyPasses;
    }

    
    return dailyPasses.filter(pass => {
      // durationFilters keys are just numbers like "2", "3", "4", etc.
      // pass.days is also a number, so we compare them directly
      const durationKey = pass.days ? pass.days.toString() : null;
      return durationKey && durationFilters[durationKey];
    });
  };

  const renderSeason = (seasonKey) => {
    const season = seasonData[seasonKey];
    
    
    if (!season || !season.dailyPasses || !season.hourlyPasses) {
      return null;
    }
    
    const isExpanded = expandedSeasons[seasonKey];
    const filteredDailyPasses = getFilteredDailyPasses(season.dailyPasses);
    
    return (
      <div key={seasonKey} className="skipass-accordion-section">
        <div 
          className={`skipass-accordion-header ${isExpanded ? 'skipass-expanded' : ''}`}
          onClick={() => handleSeasonFilter(seasonKey)}
        >
          <div className="skipass-accordion-header-content">
            <div>
              <div className="skipass-season-title">{season.title}</div>
              <div className="skipass-season-dates">{season.dates}</div>
            </div>
            <div className="skipass-season-price">
              <div className="skipass-price-info">
                <div className="skipass-price-from">Ціна від</div>
                <div className="skipass-price-amount">
                  {season.priceFrom} <span className="skipass-price-currency">₴</span>
                </div>
                <div className="skipass-price-period">день</div>
              </div>
              <span className="skipass-accordion-arrow">{isExpanded ? '^' : '^'}</span>
            </div>
          </div>
        </div>

        <div className={`skipass-accordion-content ${isExpanded ? 'skipass-expanded' : ''}`}>
            {filteredDailyPasses.length > 0 ? (
              <>
                <div className="skipass-section-title">
                  <h3>ПО ДНЯХ</h3>
                  <div className="divider"></div>
                </div>
                
                <div className="skipass-pricing-grid">
                  {filteredDailyPasses.map((pass, index) => {
                    const uniqueId = pass.skiDataId || `daily-${seasonKey}-${pass.days}-${index}`;
                    const price = pass.price && !isNaN(pass.price) ? Number(pass.price) : 0;
                    const cardId = `${uniqueId}_${price}`;
                    const isSelected = selectedCards.has(cardId);
                    const quantity = cardQuantities[cardId] || 1;
                    
                    return (
                      <div 
                        key={uniqueId} 
                        className={`skipass-pricing-card ${isSelected ? 'skipass-selected' : ''} ${!canSell || !canSellProduct(pass, seasonKey) ? 'skipass-view-only' : ''}`}
                        style={{ cursor: canSell && canSellProduct(pass, seasonKey) ? 'pointer' : 'default' }}
                      >
                        <div 
                          className="skipass-card-content"
                          onClick={() => handleProductSelect(pass, seasonKey, 'daily', index)}
                        >
                          <div className="skipass-card-left">
                            <div className="skipass-pass-type">{pass.days} дні</div>
                            <div className="skipass-card-price-amount">{pass.price} ₴</div>
                          </div>
                          <div className="skipass-card-right">
                            <div className="skipass-card-price-old">{pass.oldPrice}₴ / День</div>
                            <div className="skipass-card-savings">Економія: {pass.savings} ₴</div>
                            
                            {}
                            {canSell && canSellProduct(pass, seasonKey) && (
                              <div className="skipass-quantity-controls">
                                <button 
                                  className="skipass-qty-btn" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isSelected && quantity > 1) {
                                      decrementQuantity(cardId);
                                    } else if (isSelected && quantity === 1) {
                                      
                                      toggleCardSelection(cardId);
                                    }
                                  }}
                                  disabled={!isSelected}
                                >
                                  -
                                </button>
                                <span className="skipass-qty-display">
                                  {isSelected ? quantity : 0}
                                </span>
                                <button 
                                  className="skipass-qty-btn" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isSelected) {
                                      toggleCardSelection(cardId);
                                    } else {
                                      incrementQuantity(cardId);
                                    }
                                  }}
                                >
                                  +
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="skipass-no-results">
                <p>Немає доступних пасів для обраних фільтрів</p>
              </div>
            )}

            {season.hourlyPasses && season.hourlyPasses.length > 0 && (
              <>
            <div className="skipass-section-title">
              <h3>ПО ЧАСУ</h3>
              <div className="divider"></div>
            </div>
            
            <div className="skipass-hourly-grid">
              {season.hourlyPasses.map((pass, index) => {
                const uniqueId = pass.skiDataId || `hourly-${seasonKey}-${pass.type}-${index}`;
                const price = pass.price && !isNaN(pass.price) ? Number(pass.price) : 0;
                const cardId = `${uniqueId}_${price}`;
                const isSelected = selectedCards.has(cardId);
                const quantity = cardQuantities[cardId] || 1;
                
                return (
                  <div 
                    key={uniqueId} 
                    className={`skipass-hourly-card ${isSelected ? 'skipass-selected' : ''} ${!canSell || !canSellProduct(pass, seasonKey) ? 'skipass-view-only' : ''}`}
                    style={{ cursor: canSell && canSellProduct(pass, seasonKey) ? 'pointer' : 'default' }}
                  >
                    <div 
                      className="skipass-card-content"
                      onClick={() => handleProductSelect(pass, seasonKey, 'hourly', index)}
                    >
                      <div className="skipass-hourly-left">
                        <div className="skipass-hourly-type">{pass.type}</div>
                        {pass.time && <div className="skipass-hourly-time">{pass.time}</div>}
                      </div>
                      <div className="skipass-hourly-right">
                        {pass.subtype && <div className="skipass-hourly-subtype">{pass.subtype}</div>}
                        <div className="skipass-hourly-price">{pass.price} ₴</div>
                        
                        {}
                        {canSell && canSellProduct(pass, seasonKey) && (
                          <div className="skipass-quantity-controls">
                            <button 
                              className="skipass-qty-btn" 
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isSelected && quantity > 1) {
                                  decrementQuantity(cardId);
                                } else if (isSelected && quantity === 1) {
                                  
                                  toggleCardSelection(cardId);
                                }
                              }}
                              disabled={!isSelected}
                            >
                              -
                            </button>
                            <span className="skipass-qty-display">
                              {isSelected ? quantity : 0}
                            </span>
                            <button 
                              className="skipass-qty-btn" 
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isSelected) {
                                  toggleCardSelection(cardId);
                                } else {
                                  incrementQuantity(cardId);
                                }
                              }}
                            >
                              +
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
              </>
            )}
        </div>
      </div>
    );
  };

  
  const hasValidSeasonData = seasonData && 
    typeof seasonData === 'object' && 
    Object.keys(seasonData).length > 0;

  
  if (loading || !hasValidSeasonData) {
    return (
      <div className="skipass-loading-container">
        <div className="skipass-loading-spinner"></div>
        <p>Завантаження тарифів...</p>
      </div>
    );
  }

  
  if (error) {
    return (
      <div className="skipass-error-container">
        <div className="skipass-error-message">
          <h3>Помилка завантаження</h3>
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="skipass-retry-btn"
          >
            Спробувати знову
          </button>
        </div>
      </div>
    );
  }

  if (paymentResultOrderId) {
    return (
      <div ref={containerRef}>
        <PaymentResult 
          orderId={paymentResultOrderId} 
          onReturnHome={() => {
            setPaymentResultOrderId(null);
            setFormData({
              firstName: '',
              lastName: '',
              email: '',
              phone: ''
            });
            setFormErrors({});
            setIsTermsAccepted(false);
          }} 
          embedded={true}
        />
      </div>
    );
  }

  return (
    <div ref={containerRef}>
      {messengerConfig && Object.keys(messengerConfig).length > 0 && (
        <div className="skipass-messenger-instructions-section" style={{
          marginBottom: '1rem',
          marginLeft: '6px',
          marginRight: '6px',
          maxWidth: '1200px',
          padding: '0',
          width: '100%',
          boxSizing: 'border-box',
          clear: 'both',
          display: 'block'
        }}>
          <div className="skipass-messenger-instructions-container" style={{
            background: '#f0f9ff',
            borderRadius: '12px',
            padding: '1rem',
            border: '1px solid #bae6fd',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            width: '100%',
            boxSizing: 'border-box',
            display: 'block'
          }}>
            <h3 style={{
              margin: '0 0 0.75rem 0',
              color: '#0369a1',
              fontSize: '1.5rem',
              fontWeight: '600',
              lineHeight: '1.3'
            }}>📱 Як отримувати Скіпаси в Телеграмі:</h3>
            
            <div className="skipass-messenger-options" style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              margin: '1.5rem 0'
            }}>
              {messengerConfig.telegram && (
                <div className="skipass-messenger-option skipass-telegram" style={{
                  padding: '0.5rem',
                  background: 'white',
                  borderRadius: '8px',
                  border: '1px solid #3b82f6'
                }}>
                  <div className="skipass-messenger-content" style={{
                    textAlign: 'left'
                  }}>
                    {messengerConfig.telegram.botLink ? (
                      <>
                        <ol style={{
                          margin: '0',
                          paddingLeft: '1.5rem',
                          color: '#475569',
                          fontSize: '0.9rem',
                          lineHeight: '1.7'
                        }}>
                          <li style={{ margin: '0.5rem 0', listStyle: 'auto' }}>
                            Натисніть цю кнопку: <a 
                              href={messengerConfig.telegram.directLink || messengerConfig.telegram.botLink} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="skipass-messenger-link-btn skipass-telegram-btn"
                              style={{
                                display: 'inline-block',
                                padding: '0.5rem 1rem',
                                borderRadius: '6px',
                                textDecoration: 'none',
                                fontWeight: '600',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s',
                                textAlign: 'center',
                                border: '2px solid',
                                background: '#0088cc',
                                color: 'white',
                                borderColor: '#0088cc',
                                whiteSpace: 'nowrap',
                                marginLeft: '0.5rem'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = '#006ba3';
                                e.target.style.borderColor = '#006ba3';
                                e.target.style.transform = 'translateY(-1px)';
                                e.target.style.boxShadow = '0 4px 8px rgba(0, 136, 204, 0.3)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = '#0088cc';
                                e.target.style.borderColor = '#0088cc';
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = 'none';
                              }}
                            >
                              🔗 Відкрити Telegram бота
                            </a>
                          </li>
                          <li style={{ margin: '0.5rem 0', listStyle: 'auto' }}>Натисніть кнопку "Start"</li>
                          <li style={{ margin: '0.5rem 0', listStyle: 'auto' }}>Натисніть кнопку "Поділітися номером телефону"</li>
                        </ol>
                      </>
                    ) : (
                      <ol style={{
                        margin: '0',
                        paddingLeft: '1.5rem',
                        color: '#475569',
                        fontSize: '0.9rem',
                        lineHeight: '1.7'
                      }}>
                        <li style={{ margin: '0.5rem 0', listStyle: 'auto' }}>Відкрийте Telegram</li>
                        <li style={{ margin: '0.5rem 0', listStyle: 'auto' }}>Натисніть кнопку "Start" або надішліть <code style={{ background: '#f1f5f9', padding: '0.25rem 0.5rem', borderRadius: '4px', fontFamily: "'Courier New', monospace", fontSize: '0.85rem', color: '#0f172a' }}>/start</code></li>
                        <li style={{ margin: '0.5rem 0', listStyle: 'auto' }}>Поділіться номером телефону або надішліть: <code style={{ background: '#f1f5f9', padding: '0.25rem 0.5rem', borderRadius: '4px', fontFamily: "'Courier New', monospace", fontSize: '0.85rem', color: '#0f172a' }}>/link +ваш_номер</code></li>
                      </ol>
                    )}
                  </div>
                </div>
              )}

              {messengerConfig.viberBusiness && (() => {
                const vb = messengerConfig.viberBusiness;
                const viberLink = vb.businessChatUri
                  ? `viber://pa?chatURI=${encodeURIComponent(vb.businessChatUri)}`
                  : (vb.phoneNumber ? `viber://chat?number=${String(vb.phoneNumber || '').replace(/\D/g, '')}` : null);
                const viberButtonText = vb.businessChatName
                  ? `💼 ${(vb.openChatWithName || 'Відкрити чат: {name}').replace('{name}', vb.businessChatName)}`
                  : `💼 ${vb.openViberBusinessChat || 'Відкрити Viber Business Chat'}`;
                return (
                <>
                  <h3 style={{
                    margin: '0',
                    color: 'rgb(102, 92, 172)',
                    fontSize: '1.5rem',
                    fontWeight: '600',
                    lineHeight: '1.3'
                  }}>в Viber:</h3>
                  <div className="skipass-messenger-option skipass-viber-business" style={{
                    padding: '0.5rem',
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #665cac'
                  }}>
                  <div className="skipass-messenger-content" style={{
                    textAlign: 'left'
                  }}>
                    {viberLink ? (
                      <>
                        <ol style={{
                          margin: '0',
                          paddingLeft: '1.5rem',
                          color: '#475569',
                          fontSize: '0.9rem',
                          lineHeight: '1.7'
                        }}>
                          <li style={{ margin: '0.5rem 0', listStyle: 'auto' }}>
                            Натисніть цю кнопку: <a 
                              href={viberLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="skipass-messenger-link-btn skipass-viber-business-btn"
                              style={{
                                display: 'inline-block',
                                padding: '0.5rem 1rem',
                                borderRadius: '6px',
                                textDecoration: 'none',
                                fontWeight: '600',
                                fontSize: '0.85rem',
                                transition: 'all 0.2s',
                                textAlign: 'center',
                                border: '2px solid',
                                background: '#665cac',
                                color: 'white',
                                borderColor: '#665cac',
                                whiteSpace: 'nowrap',
                                marginLeft: '0.5rem'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = '#524a8a';
                                e.target.style.borderColor = '#524a8a';
                                e.target.style.transform = 'translateY(-1px)';
                                e.target.style.boxShadow = '0 4px 8px rgba(102, 92, 172, 0.3)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = '#665cac';
                                e.target.style.borderColor = '#665cac';
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = 'none';
                              }}
                            >
                              {viberButtonText}
                            </a>
                          </li>
                        </ol>
                      </>
                    ) : (
                      <p style={{
                        margin: '0',
                        color: '#475569',
                        fontSize: '0.9rem',
                        lineHeight: '1.7'
                      }}>
                        {(vb.openBusinessChatsAndFind || 'Відкрийте бізнес-чати та знайдіть «{name}».').replace('{name}', vb.senderName || 'бізнес-чат')}
                      </p>
                    )}
                  </div>
                </div>
                </>
                );
              })()}
            </div>

            <div className="skipass-messenger-note" style={{
              marginTop: '1.5rem',
              padding: '1rem',
              background: '#fef3c7',
              borderRadius: '8px',
              borderLeft: '4px solid #f59e0b'
            }}>
              <p style={{
                margin: '0',
                color: '#92400e',
                fontSize: '0.9rem',
                lineHeight: '1.6'
              }}>💡 Після підключення ви будете автоматично отримувати QR-коди на всі ваші замовлення!</p>
            </div>
          </div>
        </div>
      )}

      <div className="skipass-pricing-layout">
        <div className="skipass-main-content">
          <div className="skipass-seasons-accordion">
            {renderSeason('holiday')}
            {renderSeason('winter')}
            {renderSeason('low')}
          </div>

          {}
          {selectedCards.size > 0 && (
            <>
              {}
              <div className="skipass-cart-products-section">
                <ProductTable
                  selectedCards={selectedCards}
                  cardQuantities={cardQuantities}
                  findProductById={findProductById}
                  seasonData={seasonData}
                  onDeleteItem={toggleCardSelection}
                  showHeader={true}
                  className="skipass-cart-product-table"
                />
              </div>

              {/* Total and Actions Section */}
              <div className="skipass-cart-summary-section">
                <div className="skipass-cart-total-info">
                  {effectiveDiscountPercent > 0 && totalToPay > 0 ? (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem', fontSize: '0.875rem' }}>
                        <span>Проміжний підсумок</span>
                        <span>{calculateOriginalTotalPrice()} ₴</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem', fontSize: '0.875rem', color: '#166534' }}>
                        <span>Партнерська знижка (Softserve) -{effectiveDiscountPercent}%</span>
                        <span>-{calculateOriginalTotalPrice() - totalToPay} ₴</span>
                      </div>
                      <div className="skipass-cart-total-line" style={{ marginTop: '0.25rem' }}></div>
                    </>
                  ) : null}
                  <span className="skipass-cart-total-text">Сума до оплати</span>
                  <div className="skipass-cart-total-line"></div>
                  <span className="skipass-cart-total-value">{totalToPay} ₴</span>
                </div>
                
                <div className="skipass-cart-actions" ref={cartActionsRef}>
                  <button 
                    className="skipass-btn-reset-cart"
                    onClick={() => {
                      setSelectedCards(new Map());
                    }}
                  >
                    Скинути
                  </button>
                  {canSell && (
                    <button 
                      className="skipass-btn-pay-cart"
                      onClick={() => {
                        setSelectedProduct({ isMultiple: true });
                      }}
                    >
                      Оплатити
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        
        <div className="skipass-sidebar-filters">
          {/* Mobile Filter Accordions */}
          <div className="skipass-mobile-filter-accordions">
            {/* Season Filter Accordion */}
            <div className="skipass-mobile-filter-accordion">
              <button 
                className="skipass-mobile-filter-accordion-btn"
                onClick={() => setShowSeasonFilters(!showSeasonFilters)}
              >
                <span className={`skipass-filter-accordion-arrow ${showSeasonFilters ? 'skipass-expanded' : ''}`}>
                  <span>↑</span>
                  <span>↓</span>
                </span>
                <span>СЕЗОН КАТАННЯ</span>
              </button>
              {showSeasonFilters && (
                <div className="skipass-mobile-filter-content">
                  <div className="skipass-filter-checkboxes">
                    <div className="skipass-checkbox-item">
                      <input
                        type="checkbox"
                        id="holiday-season-accordion"
                        checked={selectedFilters.holiday}
                        onChange={() => handleSeasonFilter('holiday')}
                      />
                      <label htmlFor="holiday-season-accordion">Святковий сезон</label>
                    </div>
                    <div className="skipass-checkbox-item">
                      <input
                        type="checkbox"
                        id="winter-season-accordion"
                        checked={selectedFilters.winter}
                        onChange={() => handleSeasonFilter('winter')}
                      />
                      <label htmlFor="winter-season-accordion">Зимовий сезон</label>
                    </div>
                    <div className="skipass-checkbox-item">
                      <input
                        type="checkbox"
                        id="low-season-accordion"
                        checked={selectedFilters.low}
                        onChange={() => handleSeasonFilter('low')}
                      />
                      <label htmlFor="low-season-accordion">Низький сезон</label>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {}
            <div className="skipass-mobile-filter-accordion">
              <div className="skipass-mobile-filter-header">
                <button 
                  className="skipass-mobile-filter-accordion-btn"
                  onClick={() => setShowDurationFilters(!showDurationFilters)}
                >
                  <span className={`skipass-filter-accordion-arrow ${showDurationFilters ? 'skipass-expanded' : ''}`}>
                    <span>↑</span>
                    <span>↓</span>
                  </span>
                  <span>ПО ДНЯХ</span>
                </button>
                {hasActiveDurationFilters && (
                  <button 
                    onClick={clearDurationFilters}
                    className="skipass-clear-filters-btn"
                  >
                    Очистити
                  </button>
                )}
              </div>
              {showDurationFilters && (
                <div className="skipass-mobile-filter-content">
                  <div className="skipass-filter-checkboxes">
                    {availableDurations.map((duration) => {
                      const days = parseInt(duration);
                      return (
                        <div key={duration} className="skipass-checkbox-item">
                      <input
                        type="checkbox"
                            id={`${duration}-accordion`}
                            checked={durationFilters[duration] || false}
                            onChange={() => handleDurationFilter(duration)}
                      />
                          <label htmlFor={`${duration}-accordion`}>{days} {days === 1 ? 'день' : days < 5 ? 'дні' : 'днів'}</label>
                    </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {}
          <div className="skipass-filters">
            {}
            <div className="filter-section">
              <h3>СЕЗОН КАТАННЯ</h3>
              <div className="skipass-filter-checkboxes">
                <div className="skipass-checkbox-item">
                  <input
                    type="checkbox"
                    id="holiday-filter"
                    checked={selectedFilters.holiday}
                    onChange={() => handleSeasonFilter('holiday')}
                  />
                  <label htmlFor="holiday-filter">Святковий сезон</label>
                </div>
                <div className="skipass-checkbox-item">
                  <input
                    type="checkbox"
                    id="winter-filter"
                    checked={selectedFilters.winter}
                    onChange={() => handleSeasonFilter('winter')}
                  />
                  <label htmlFor="winter-filter">Зимовий сезон</label>
                </div>
                <div className="skipass-checkbox-item">
                  <input
                    type="checkbox"
                    id="low-filter"
                    checked={selectedFilters.low}
                    onChange={() => handleSeasonFilter('low')}
                  />
                  <label htmlFor="low-filter">Низький сезон</label>
                </div>
              </div>
            </div>

            {}
            <div className="filter-section">
              <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>ПО ДНЯХ</h3>
                {hasActiveDurationFilters && (
                  <button 
                    onClick={clearDurationFilters}
                    className="skipass-clear-filters-btn"
                  >
                    Очистити
                  </button>
                )}
              </div>
              <div className="skipass-filter-checkboxes">
                {availableDurations.map((duration) => {
                  const days = parseInt(duration);
                  return (
                    <div key={duration} className="skipass-checkbox-item">
                  <input
                    type="checkbox"
                        id={`${duration}-filter`}
                        checked={durationFilters[duration] || false}
                        onChange={() => handleDurationFilter(duration)}
                  />
                      <label htmlFor={`${duration}-filter`}>{days} {days === 1 ? 'день' : days < 5 ? 'дні' : 'днів'}</label>
                </div>
                  );
                })}
                </div>
                </div>
                </div>
        </div>
      </div>

      {selectedProduct && (
        <div 
          className="skipass-selected-product-form"
          onClick={(e) => {
            if (e.target.classList.contains('selected-product-form')) {
              setSelectedProduct(null);
              setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phone: ''
              });
              setFormErrors({});
              setIsTermsAccepted(false);
              setDiscountValidated(false);
              setDiscountPercentApplied(0);
              setDiscountCode('');
              setDiscountCodeSent(false);
              setDiscountError('');
              setDiscountCheck(prev => ({ ...prev, alreadyValidated: false, maxAttemptsReached: false }));
              discountSummaryRef.current = null;
            }
          }}
        >
          <div className="skipass-form-page">
            <div className="skipass-form-content-wrapper">
              {}
              <div className="skipass-form-main-section">
                <>
                  <div className="skipass-resort-header">
                    <h1>Гірськолижний курорт ПЛАЙ</h1>
                  </div>
                  
                  <div className="skipass-auth-section">
                    <h3>Авторизація ( не обов'язково )</h3>
                    <div className="skipass-auth-divider"></div>
                  </div>
                  
                  <div className="skipass-form-section-header">
                    <h2>Ваші дані</h2>
                    <p>Інформація для купівлі SKI PASS</p>
                  </div>
                </>

                <form className="skipass-booking-form">
                  <div className="skipass-form-row">
                    <div className="skipass-form-group skipass-half-width">
                      <label htmlFor="firstName">Ім'я</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                        className={formErrors.firstName ? 'skipass-error' : ''}
                      />
                      {formErrors.firstName && <span className="skipass-error-message">{formErrors.firstName}</span>}
                    </div>

                    <div className="skipass-form-group skipass-half-width">
                      <label htmlFor="lastName">Прізвище</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                        className={formErrors.lastName ? 'skipass-error' : ''}
                      />
                      {formErrors.lastName && <span className="skipass-error-message">{formErrors.lastName}</span>}
                    </div>
                  </div>

                  <div className="skipass-form-group skipass-full-width">
                    <label htmlFor="email">E-mail*</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({...formData, email: e.target.value});
                        // Clear error when user starts typing
                        if (formErrors.email) {
                          setFormErrors({...formErrors, email: ''});
                        }
                      }}
                      onBlur={() => {
                        if (!formData.email) {
                          setFormErrors({...formErrors, email: "Email є обов'язковим"});
                        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
                          setFormErrors({...formErrors, email: "Введіть коректний email"});
                        }
                      }}
                      className={formErrors.email ? 'skipass-error' : ''}
                      required
                    />
                    {formErrors.email && <span className="skipass-error-message">{formErrors.email}</span>}
                  </div>

                  {discountCheck.enabled && discountCheck.eligible && (
                    <div className="skipass-form-group skipass-full-width" style={{ marginTop: '0.5rem', padding: '0.75rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                      <div style={{ marginBottom: '0.5rem', fontWeight: 600, color: '#166534' }}>Партнерська знижка (Softserve)</div>
                      {discountCheck.maxAttemptsReached ? (
                        <p style={{ margin: 0, color: '#b91c1c', fontSize: '0.875rem' }} role="alert">
                          Ви вже використали максимальну кількість знижок на цей рік.
                        </p>
                      ) : !(discountValidated || discountCheck.alreadyValidated) ? (
                        <>
                          <button
                            type="button"
                            onClick={async () => {
                              setDiscountError('');
                              setDiscountAlreadyHasCode(false);
                              setSendingDiscountCode(true);
                              try {
                                const res = await fetch(`${API_CONFIG.baseURL}/api/discount/send-code`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ email: formData.email.trim().toLowerCase() })
                                });
                                const data = await res.json();
                                if (data.success && data.data) {
                                  const alreadyHadCode = !!data.data.alreadyHasCode;
                                  setDiscountAlreadyHasCode(alreadyHadCode);
                                  setDiscountCodeSent(true);
                                  setDiscountCode('');
                                  if (alreadyHadCode) {
                                    const pct = Number(data.data.percent) || discountCheck.percent || 10;
                                    discountSummaryRef.current = { hasDiscount: true, percent: pct };
                                    setDiscountValidated(true);
                                    setDiscountPercentApplied(pct);
                                  }
                                } else {
                                  setDiscountError(data.message || 'Не вдалося надіслати код');
                                }
                              } catch (e) {
                                setDiscountError('Помилка мережі');
                              } finally {
                                setSendingDiscountCode(false);
                              }
                            }}
                            disabled={sendingDiscountCode || !formData.email}
                            style={{ padding: '0.5rem 1rem', marginBottom: '0.5rem', background: '#166534', color: 'white', border: 'none', borderRadius: '6px', cursor: sendingDiscountCode ? 'not-allowed' : 'pointer', fontWeight: 500 }}
                          >
                            {sendingDiscountCode ? '...' : 'Надіслати мені код знижки'}
                          </button>
                          {discountCodeSent && (
                            <p style={{ margin: '0.25rem 0', color: '#166534', fontSize: '0.875rem' }}>
                              Код надіслано на вашу пошту. Введіть його нижче та натисніть «Перевірити».
                            </p>
                          )}
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                            <input
                              type="text"
                              placeholder="Код знижки"
                              value={discountCode}
                              onChange={(e) => { setDiscountCode(e.target.value.toUpperCase()); setDiscountError(''); }}
                              style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #86efac', maxWidth: '160px' }}
                            />
                            <button
                              type="button"
                              onClick={async () => {
                                setDiscountError('');
                                setValidatingDiscountCode(true);
                                try {
                                  const res = await fetch(`${API_CONFIG.baseURL}/api/discount/validate`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ email: formData.email.trim().toLowerCase(), code: discountCode.trim() })
                                  });
                                  const data = await res.json();
                                  if (data.success && data.data) {
                                    if (data.data.valid) {
                                      const pct = Number(data.data.discountPercent) ?? Number(data.data.percent) ?? discountCheck.percent ?? 10;
                                      discountSummaryRef.current = { hasDiscount: true, percent: pct };
                                      setDiscountValidated(true);
                                      setDiscountPercentApplied(pct);
                                    } else {
                                      setDiscountError(data.data.message || 'Невірний або протермінований код');
                                    }
                                  } else {
                                    setDiscountError(data.message || 'Помилка перевірки');
                                  }
                                } catch (e) {
                                  setDiscountError('Помилка перевірки');
                                } finally {
                                  setValidatingDiscountCode(false);
                                }
                              }}
                              disabled={validatingDiscountCode || !discountCode.trim()}
                              style={{ padding: '0.5rem 1rem', background: '#22c55e', color: 'white', border: 'none', borderRadius: '6px', cursor: validatingDiscountCode || !discountCode.trim() ? 'not-allowed' : 'pointer', fontWeight: 500 }}
                            >
                              {validatingDiscountCode ? '...' : 'Перевірити'}
                            </button>
                          </div>
                          {discountError && <p style={{ margin: '0.25rem 0', color: '#b91c1c', fontSize: '0.875rem' }} role="alert">{discountError}</p>}
                        </>
                      ) : (
                        <p style={{ margin: 0, color: '#166534', fontSize: '0.875rem' }}>
                          {discountCheck.alreadyValidated ? 'Ви вже підтвердили код для цієї пошти. Знижка застосована.' : `Код перевірено успішно. Знижка ${discountPercentApplied || discountCheck.percent || 10}% застосована, ціни оновлено.`}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="skipass-form-group skipass-full-width">
                    <label htmlFor="phone">Номер телефону*</label>
                    <PhoneInput
                      placeholder="Введіть номер телефону"
                      value={formData.phone}
                      onChange={(value) => {
                        setFormData({...formData, phone: value || ''});
                        // Clear error when user starts typing
                        if (formErrors.phone) {
                          setFormErrors({...formErrors, phone: ''});
                        }
                      }}
                      onBlur={() => {
                        if (!formData.phone) {
                          setFormErrors({...formErrors, phone: "Номер телефону є обов'язковим"});
                        } else if (!isValidPhoneNumber(formData.phone)) {
                          setFormErrors({...formErrors, phone: "Введіть коректний номер телефону"});
                        } else {
                          try {
                            // Additional validation using libphonenumber-js
                            const phoneNumber = parsePhoneNumber(formData.phone);
                            const nationalLength = phoneNumber.nationalNumber.length;
                            const country = phoneNumber.country;
                            
                            // Country-specific validation
                            if (country === 'UA') {
                              // Ukrainian numbers should have exactly 9 digits in national part
                              if (nationalLength !== 9) {
                                setFormErrors({...formErrors, phone: "Український номер повинен містити 9 цифр після коду країни"});
                              }
                            } else {
                              // General validation for other countries
                              if (nationalLength > 10) {
                                setFormErrors({...formErrors, phone: "Номер телефону занадто довгий для цієї країни"});
                              } else if (nationalLength < 6) {
                                setFormErrors({...formErrors, phone: "Номер телефону занадто короткий для цієї країни"});
                              }
                            }
                          } catch (parseError) {
                            // Fallback to simple digit counting if parsing fails
                            const digitsOnly = formData.phone.replace(/\D/g, '');
                            if (digitsOnly.length > 13) {
                              setFormErrors({...formErrors, phone: "Номер телефону занадто довгий"});
                            } else if (digitsOnly.length < 7) {
                              setFormErrors({...formErrors, phone: "Номер телефону занадто короткий"});
                            }
                          }
                        }
                      }}
                      defaultCountry="UA"
                      className={formErrors.phone ? 'skipass-phone-input-container skipass-error' : 'skipass-phone-input-container'}
                      international
                      countryCallingCodeEditable={false}
                      required
                    />
                    {formErrors.phone && <span className="skipass-error-message">{formErrors.phone}</span>}
                  </div>

                  <div className="skipass-form-group skipass-checkbox-group">
                    <label className="skipass-checkbox-label">
                      <input 
                        type="checkbox" 
                        checked={isTermsAccepted}
                        onChange={(e) => {
                          setIsTermsAccepted(e.target.checked);
                          
                          if (formErrors.terms && e.target.checked) {
                            setFormErrors({...formErrors, terms: ''});
                          }
                        }}
                        required 
                      />
                      <span className="skipass-checkmark"></span>
                      <span>
                        Ознайомлений та згодний з{' '}
                        <a 
                          href="https://play-karpaty.com/pravyla-povedinky-na-girskyh-trasah-2/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="skipass-terms-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          правилами придбання і користування скі-пасами в гірськолижному комплексі "Плай"
                        </a>
                      </span>
                    </label>
                    {formErrors.terms && <span className="skipass-error-message">{formErrors.terms}</span>}
                  </div>

                  <div className="skipass-form-actions">
                    <button
                      type="button"
                      className="skipass-btn-back"
                      onClick={() => {
                        setSelectedProduct(null);
                        setFormData({
                          firstName: '',
                          lastName: '',
                          email: '',
                          phone: ''
                        });
                        setFormErrors({});
                        setIsTermsAccepted(false);
                        setDiscountValidated(false);
                        setDiscountPercentApplied(0);
                        setDiscountCode('');
                        setDiscountCodeSent(false);
                        setDiscountError('');
                        setDiscountCheck(prev => ({ ...prev, alreadyValidated: false, maxAttemptsReached: false }));
                        discountSummaryRef.current = null;
                      }}
                    >
                      Повернутись до вибору
                    </button>
                    {calculateTotalPrice() > 0 && (
                      <button
                        type="button"
                        className={`skipass-btn-confirm ${isFormValid() ? 'skipass-enabled' : 'skipass-disabled'}`}
                        disabled={!isFormValid()}
                        onClick={async () => {
                          if (!validateForm()) {
                            return;
                          }
                        
                        const isInIframe = window.self !== window.top;
                        let paymentWindow = null;
                        
                        paymentWindow = window.open(
                          'about:blank',
                          'liqpay-checkout',
                          'width=800,height=600,scrollbars=yes,resizable=yes'
                        );
                        if (paymentWindow) {
                          try {
                            const d = paymentWindow.document;
                            d.open();
                            d.write('<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Оплата</title><style>body{font-family:system-ui,-apple-system,sans-serif;display:flex;align-items:center;justify-content:center;min-height:100vh;margin:0;background:#f4f6f8;color:#1a1a1a}p{margin:0;font-size:15px}</style></head><body><p>Підготовка до оплати…</p></body></html>');
                            d.close();
                          } catch (e) {
                          }
                        }
                        
                        try {
                          const totalPrice = calculateTotalPrice();
                          const originalTotalForOrder = (discountValidated || discountCheck.alreadyValidated) ? calculateOriginalTotalPrice() : null;
                          const items = Array.from(selectedCards).map(cardId => {
                            const quantity = cardQuantities[cardId] || 0;
                            const product = findProductById(cardId);
                            const productPrice = product && product.price && !isNaN(product.price) ? Number(product.price) : 0;
                            return {
                              cardId,
                              product,
                              quantity,
                              total: productPrice * quantity
                            };
                          }).filter(item => item.quantity > 0);

                          if (items.length === 0) {
                            if (paymentWindow && !paymentWindow.closed) paymentWindow.close();
                            alert('Будь ласка, виберіть хоча б один товар');
                            return;
                          }

                          // Helper function to find season for a product
                          const findSeasonForProduct = (product) => {
                            if (!seasonData || !product) return { seasonKey: 'holiday', seasonTitle: 'Святковий сезон' };
                            
                            const productPrice = product.price && !isNaN(product.price) ? Number(product.price) : 0;
                            const skiDataId = product.skiDataId || product.productId || product.skidataProductId || product.skidataId;
                            
                            for (const seasonKey of Object.keys(seasonData)) {
                              const season = seasonData[seasonKey];
                              if (season.dailyPasses?.some(p => {
                                  const pPrice = p.price && !isNaN(p.price) ? Number(p.price) : 0;
                                  return p.skiDataId === skiDataId && pPrice === productPrice;
                                }) || 
                                  season.hourlyPasses?.some(p => {
                                  const pPrice = p.price && !isNaN(p.price) ? Number(p.price) : 0;
                                  return p.skiDataId === skiDataId && pPrice === productPrice;
                                })) {
                                return {
                                  seasonKey: seasonKey,
                                  seasonTitle: season.title || 
                                    (seasonKey === 'holiday' ? 'Святковий сезон' : 
                                     seasonKey === 'winter' ? 'Зимовий сезон' : 'Низький сезон')
                                };
                              }
                            }
                            
                            return { seasonKey: 'holiday', seasonTitle: 'Святковий сезон' };
                          };
                          
                          // Build orderInfo array with all selected items
                          const orderInfo = items.map(item => {
                            const product = item.product;
                            if (!product) {
                              console.error('❌ Product not found for item:', item);
                              return null;
                            }
                            
                            const { seasonKey, seasonTitle } = findSeasonForProduct(product);
                            
                            // Get productId from various possible fields
                            const productId = product.skiDataId || product.productId || product.skidataProductId || product.skidataId;
                            
                            if (!productId) {
                              console.error('❌ ProductId not found in product:', product);
                            }
                            
                            return {
                              productId: productId,
                              amount: product.price * item.quantity,
                              price: product.price,
                              quantity: item.quantity,
                              productType: product.days ? 'daily' : 'hourly',
                              productName: product.days ? `${product.days} днів` : product.type,
                              seasonKey: seasonKey,
                              seasonTitle: seasonTitle,
                              currency: 'UAH',
                              ...(product.days && { days: product.days }),
                              ...(product.time && { time: product.time }),
                              ...(product.subtype && { subtype: product.subtype }),
                              ...(product.savings && { savings: product.savings }),
                              // Add SKIDATA fields
                              ...(product.skiDataId && { skiDataId: product.skiDataId }),
                              ...(product.consumerCategoryId && { consumerCategoryId: product.consumerCategoryId }),
                              ...(product.validityRange && { validityRange: product.validityRange })
                            };
                          }).filter(item => item !== null); // Remove null items
                          
                          // Use first item for backward compatibility with productInfo
                          const firstItem = items[0];
                          const product = firstItem.product;
                          const { seasonKey, seasonTitle } = findSeasonForProduct(product);
                          
                          const orderPayload = {
                            customerInfo: {
                              firstName: formData.firstName,
                              lastName: formData.lastName,
                              email: formData.email,
                              phone: formData.phone
                            },
                            orderInfo: orderInfo, // Send all items as orderInfo array
                            ...((discountValidated || discountCheck.alreadyValidated) && {
                              applyPartnerDiscount: true,
                              ...(discountCode && discountCode.trim() && { discountCode: discountCode.trim() }),
                              ...(originalTotalForOrder != null && originalTotalForOrder > 0 && { originalAmount: originalTotalForOrder })
                            }),
                            productInfo: {
                              productType: product.days ? 'daily' : 'hourly',
                              productName: product.days ? `${product.days} днів` : product.type,
                              seasonKey: seasonKey,
                              seasonTitle: seasonTitle,
                              price: product.price,
                              currency: 'UAH',
                              ...(product.days && { days: product.days }),
                              ...(product.time && { time: product.time }),
                              ...(product.subtype && { subtype: product.subtype }),
                              ...(product.savings && { savings: product.savings }),
                              // Add SKIDATA fields
                              ...(product.skiDataId && { skiDataId: product.skiDataId }),
                              ...(product.consumerCategoryId && { consumerCategoryId: product.consumerCategoryId }),
                              ...(product.validityRange && { validityRange: product.validityRange })
                            },
                            paymentInfo: {
                              amount: totalPrice,
                              currency: 'UAH',
                              paymentMethod: 'LiqPay',
                              liqpayOrderId: `SKI_${Date.now()}`
                            }
                          };

                          const ordersUrl = canSellParam === 'false' 
                            ? `${apiUrls.orders}?can_sell=false`
                            : apiUrls.orders;
                          const orderResponse = await fetch(ordersUrl, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(orderPayload)
                          });

                          if (!orderResponse.ok) {
                            throw new Error('Failed to create order');
                          }

                          const orderResult = await orderResponse.json();
                          const orderId = orderResult.data.orderId;

                          // Check if running in iframe
                          const isInIframe = window.self !== window.top;
                          
                          // Get parent URL if in iframe (for returning to original page after payment)
                          let parentUrl = null;
                          if (isInIframe) {
                            try {
                              parentUrl = window.top.location.href;
                            } catch (e) {
                              // Can't access parent URL (cross-origin), use document.referrer as fallback
                              if (document.referrer) {
                                parentUrl = document.referrer;
                              }
                            }
                          }
                          
                          const checkoutPayload = {
                            orderId: orderId,
                            amount: totalPrice,
                            currency: 'UAH',
                            description: `Ski Pass Purchase - ${items.length} item(s)`,
                            customerName: `${formData.firstName} ${formData.lastName}`,
                            customerFirstName: formData.firstName,
                            customerLastName: formData.lastName,
                            customerEmail: formData.email,
                            customerPhone: formData.phone,
                            isInIframe: isInIframe, // Pass iframe context to backend
                            parentUrl: parentUrl, // Pass parent URL to return after payment
                            products: orderInfo
                          };

                          const checkoutUrl = canSellParam === 'false' 
                            ? `${apiUrls.ordersCheckout}?can_sell=false`
                            : apiUrls.ordersCheckout;
                          const checkoutResponse = await fetch(checkoutUrl, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(checkoutPayload)
                          });

                          const checkoutResult = await checkoutResponse.json();

                          if (!checkoutResponse.ok || !checkoutResult.success) {
                            const errorMsg = checkoutResult.error || checkoutResult.message || 'Failed to generate payment URL';
                            const hint = checkoutResult.details?.hint || '';
                            throw new Error(`${errorMsg}${hint ? '\n\n' + hint : ''}`);
                          }
                          
                          const responseData = checkoutResult.data || {};
                          const actionUrl = responseData.url || checkoutResult.url;

                          // 1. Define Helper Functions (Scoped here for simplicity)
                          const submitPostInPopup = (win, url, params) => {
                            if (!win || win.closed) return;
                            const doc = win.document;
                            const inputs = Object.keys(params).map(k =>
                              `<input type="hidden" name="${k}" value="${String(params[k]).replace(/"/g, '&quot;')}" />`
                            ).join('');
                            doc.open();
                            doc.write(`<html><head><meta charset="UTF-8"></head><body><div style="text-align:center;margin-top:20px;">Redirecting to payment...</div><form id="f" method="POST" action="${url}">${inputs}</form><script>document.getElementById('f').submit()</script></body></html>`);
                            doc.close();
                          };

                          const submitPostInCurrentWindow = (url, params) => {
                             const form = document.createElement('form');
                             form.method = 'POST';
                             form.action = url;
                             Object.keys(params).forEach(k => {
                               const input = document.createElement('input');
                               input.type = 'hidden';
                               input.name = k;
                               input.value = params[k];
                               form.appendChild(input);
                             });
                             document.body.appendChild(form);
                             form.submit();
                          };

                          // 2. Prepare Payment Parameters
                          let paymentParams = {};
                          if (responseData.params) {
                            // AssetPayments (Generic Params)
                            paymentParams = responseData.params;
                          } else {
                            // Legacy LiqPay (Specific fields)
                            const d = responseData.data || responseData.dataBase64 || checkoutResult.dataBase64;
                            const s = responseData.signature || checkoutResult.signature;
                            if (d) paymentParams.data = d;
                            if (s) paymentParams.signature = s;
                          }

                          // Check if we should use POST (if params exist)
                          const isPost = Object.keys(paymentParams).length > 0;

                          if (actionUrl) {
                            localStorage.setItem('currentOrderId', orderId);
                            localStorage.setItem('orderData', JSON.stringify(orderResult.data));
                            
                            // 3. Execute Redirect
                            if (isPost) {
                                // --- POST FLOW (AssetPayments & Standard LiqPay) ---
                            if (paymentWindow) {
                              try {
                                    submitPostInPopup(paymentWindow, actionUrl, paymentParams);
                              } catch (e) {
                                     // If reference lost or closed, try to reopen
                                if (paymentWindow.closed) {
                                        paymentWindow = window.open('about:blank', 'liqpay-checkout', 'width=800,height=600,scrollbars=yes,resizable=yes');
                                        if (paymentWindow) submitPostInPopup(paymentWindow, actionUrl, paymentParams);
                                } else {
                                        submitPostInPopup(paymentWindow, actionUrl, paymentParams);
                                     }
                                  }
                                }
                            
                                // Fallback if popup blocked/closed
                                if (!paymentWindow || paymentWindow.closed) {
                                    if (isInIframe) {
                                        try {
                                            // Request parent to handle POST
                                            window.parent.postMessage({
                                              type: 'liqpay-checkout',
                                              method: 'POST',
                                              url: actionUrl,
                                              params: paymentParams
                                            }, '*');
                                        } catch (e) {
                                            submitPostInCurrentWindow(actionUrl, paymentParams);
                                        }
                                    } else {
                                        submitPostInCurrentWindow(actionUrl, paymentParams);
                                    }
                                }
                            } else {
                                // --- GET FLOW (Legacy/Fallback) ---
                                if (paymentWindow) {
                                   try {
                                     paymentWindow.location.href = actionUrl;
                                   } catch(e) {
                                     if (paymentWindow.closed) {
                                       paymentWindow = window.open(actionUrl, 'liqpay-checkout', 'width=800,height=600,scrollbars=yes,resizable=yes');
                                     } else {
                                       paymentWindow.location.replace(actionUrl);
                                }
                              }
                            }
                            
                            if (!paymentWindow || paymentWindow.closed) {
                              if (isInIframe) {
                                      try { window.parent.postMessage({ type: 'liqpay-checkout', url: actionUrl }, '*'); }
                                      catch (e) { window.location.href = actionUrl; }
                                   } else {
                                      window.location.href = actionUrl;
                                }
                              }
                            }
                                
                            // 4. Start Monitoring (Common for both)
                            if (paymentWindow && !paymentWindow.closed) {
                              const checkPaymentWindow = setInterval(() => {
                                if (paymentWindow.closed) {
                                  clearInterval(checkPaymentWindow);
                                
                                  // Window closed -> Start checking status immediately
                                  setPaymentResultOrderId(orderId);
                                  setSelectedCards(new Set());
                                  setCardQuantities({});
                                  setSelectedProduct(null);
                                  
                                  const checkStatus = async (attempt = 1, maxAttempts = 5) => {
                                    try {
                                      const orderResponse = await fetch(`${API_CONFIG.baseURL}/api/orders/payment-status/${orderId}`);
                                      if (orderResponse.ok) {
                                        const orderResult = await orderResponse.json();
                                        if (orderResult.success && orderResult.data) {
                                          const orderData = orderResult.data;
                                          setPaymentResultOrderId(orderData.orderId);
                                          
                                          if (window.__SKI_PASS_WEB_COMPONENT__) {
                                            window.postMessage({ type: 'show-confirmation', orderId: orderData.orderId }, '*');
                                          }
                                        } else if (attempt < maxAttempts) {
                                          setTimeout(() => checkStatus(attempt + 1, maxAttempts), 2000 * attempt);
                                        }
                                      } else if (attempt < maxAttempts) {
                                        setTimeout(() => checkStatus(attempt + 1, maxAttempts), 2000 * attempt);
                                      }
                                    } catch (error) {
                                      if (attempt < maxAttempts) setTimeout(() => checkStatus(attempt + 1, maxAttempts), 2000 * attempt);
                                      }
                                    }
                                  setTimeout(() => checkStatus(), 300);
                                }
                              }, 1000);
                              
                              const messageHandler = (event) => {
                                if (event.data && event.data.type === 'show-confirmation' && event.data.orderId) {
                                  clearInterval(checkPaymentWindow);
                                  window.removeEventListener('message', messageHandler);
                                  setPaymentResultOrderId(event.data.orderId);
                                          setSelectedCards(new Set());
                                          setCardQuantities({});
                                          setSelectedProduct(null);
                                        }
                                      }
                              window.addEventListener('message', messageHandler);
                              setTimeout(() => {
                                clearInterval(checkPaymentWindow);
                                window.removeEventListener('message', messageHandler);
                              }, 30 * 60 * 1000);
                            }
                          } else {
                            throw new Error('Payment URL not generated in response');
                          }
                        } catch (error) {
                          console.error('Payment error:', error);
                          const errorMessage = error.message || 'Невідома помилка';
                          alert(`Помилка при створенні замовлення:\n\n${errorMessage}`);
                        }
                      }}
                    >
                      Оплатити
                    </button>
                    )}
                  </div>
                </form>
              </div>

              {/* Right Column - Order Summary */}
              <div className="skipass-order-summary-section">
                <div className="skipass-order-header">
                  <h2>Ваше замовлення</h2>
                </div>
                
                {/* Ski Pass Cards Image */}
                <div className="skipass-ski-pass-cards">
                  <div className="skipass-pass-card-stack">
                    <div className="skipass-pass-card skipass-pass-card-1"></div>
                    <div className="skipass-pass-card skipass-pass-card-2"></div>
                    <div className="skipass-pass-card skipass-pass-card-3"></div>
                  </div>
                </div>
                
                {/* Order Items Table */}
                <div className="skipass-order-table">
                  <div className="skipass-table-header">
                    <span className="skipass-header-product">Товар</span>
                    <span className="skipass-header-subtotal">Проміжний підсумок</span>
                  </div>
                  
                  <div className="skipass-table-body">
                    {Array.from(selectedCards).map(cardId => {
                      const quantity = cardQuantities[cardId] || 0;
                      if (quantity === 0) return null;

                      const product = findProductById(cardId);
                      if (!product) return null;

                      const productPrice = product.price && !isNaN(product.price) ? Number(product.price) : 0;
                      const itemTotal = productPrice * quantity;

                      // Find season for display
                      let season = null;
                      let seasonName = '';
                      // Extract skiDataId from cardId (format: "skiDataId_price")
                      const cardParts = cardId.split('_');
                      const cardPriceStr = cardParts.pop();
                      const cardSkiDataId = cardParts.join('_');
                      
                      for (const seasonKey of Object.keys(seasonData)) {
                        const s = seasonData[seasonKey];
                        const cardProductPrice = productPrice;
                        if (s.dailyPasses?.some(p => {
                            const pPrice = p.price && !isNaN(p.price) ? Number(p.price) : 0;
                            return p.skiDataId === cardSkiDataId && pPrice === cardProductPrice;
                          }) || 
                            s.hourlyPasses?.some(p => {
                            const pPrice = p.price && !isNaN(p.price) ? Number(p.price) : 0;
                            return p.skiDataId === cardSkiDataId && pPrice === cardProductPrice;
                          })) {
                          season = s;
                          seasonName = s.title || 
                        (seasonKey === 'holiday' ? 'Святковий сезон' : 
                         seasonKey === 'winter' ? 'Зимовий сезон' : 'Низький сезон');
                          break;
                        }
                      }

                      return (
                        <div key={cardId} className="skipass-table-row">
                          <div className="skipass-product-info">
                            {product.days ? `${product.days} ${product.days === 1 ? 'день повний' : product.days >= 2 && product.days <= 4 ? 'дні' : 'днів'}` : product.type} 
                            {product.time ? ` ${product.time}` : ''}
                            {product.subtype ? ` ${product.subtype}` : ''}
                            {seasonName && ` - ${seasonName}`}
                            <span className="skipass-quantity-multiplier">* {quantity}</span>
                          </div>
                          <div className="skipass-product-actions">
                            <span className="skipass-product-price">{itemTotal.toFixed(0)} ₴</span>
                            {(
                              <button 
                                className="skipass-delete-item-btn"
                                onClick={() => {
                                  
                                  const newSelectedCards = new Set(selectedCards);
                                  newSelectedCards.delete(cardId);
                                  setSelectedCards(newSelectedCards);
                                  
                                  const newQuantities = {...cardQuantities};
                                  delete newQuantities[cardId];
                                  setCardQuantities(newQuantities);
                                }}
                                title="Видалити товар"
                              >
                                <DeleteIcon fontSize="small" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="skipass-table-footer" key={`footer-${discountValidated}-${discountCheck.alreadyValidated}-${discountCheck.percent}-${totalToPay}`}>
                    {(() => {
                      const fromRef = discountSummaryRef.current;
                      const hasDiscount = fromRef?.hasDiscount === true || discountValidated || discountCheck.alreadyValidated;
                      const pct = hasDiscount ? (fromRef?.percent ?? discountPercentApplied ?? discountCheck.percent ?? 10) : 0;
                      const original = calculateOriginalTotalPrice();
                      const discountedTotal = pct > 0 ? Math.round(original * (1 - pct / 100)) : original;
                      const totalToShow = pct > 0 ? discountedTotal : original;
                      const showDiscountBreakdown = hasDiscount && pct > 0 && original > 0;
                      if (!showDiscountBreakdown) {
                        return (
                          <div className="skipass-total-row">
                            <span className="skipass-total-label">Сума до оплати</span>
                            <span className="skipass-total-amount">{totalToShow} ₴</span>
                          </div>
                        );
                      }
                      return (
                        <>
                          <div className="skipass-total-row" style={{ fontSize: '0.9rem' }}>
                            <span className="skipass-total-label">Проміжний підсумок</span>
                            <span className="skipass-total-amount">{original} ₴</span>
                          </div>
                          <div className="skipass-total-row" style={{ fontSize: '0.9rem', color: '#166534' }}>
                            <span className="skipass-total-label">Партнерська знижка (Softserve) -{pct}%</span>
                            <span className="skipass-total-amount">-{original - discountedTotal} ₴</span>
                          </div>
                          <div className="skipass-total-row">
                            <span className="skipass-total-label">Сума до оплати</span>
                            <span className="skipass-total-amount">{discountedTotal} ₴</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PricingPageSimple;